<template>
  <div class="max-w-5xl mx-auto space-y-8">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="120px" label-position="top">
      
      <!-- 成果归属 -->
      <AchievementTypeSection
        :achievementType="formData.achievementType"
        :teamMembers="formData.teamMembers"
        @update:achievementType="handleAchievementTypeChange"
        @update:teamMembers="handleTeamMembersChange"
      />
      
    
      
      <!-- 获奖基本信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">获奖基本信息</h3>
        <div class="space-y-4">
          <el-form-item label="获奖名称" prop="honors_name" required>
            <el-input
              v-model="formData.honors_name"
              placeholder="请输入获奖名称"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="获奖级别" prop="honors_level" required>
            <el-select v-model="formData.honors_level" placeholder="请选择获奖级别" class="custom-select">
              <el-option value="国家级" label="国家级" />
              <el-option value="省部级" label="省部级" />
              <el-option value="市级" label="市级" />
              <el-option value="校级" label="校级" />
              <el-option value="其他" label="其他" />
            </el-select>
          </el-form-item>
          <el-form-item label="获奖类型" prop="honors_type" required>
            <el-select v-model="formData.honors_type" placeholder="请选择获奖类型" class="custom-select">
              <el-option value="科技奖" label="科技奖" />
              <el-option value="教学奖" label="教学奖" />
              <el-option value="创新奖" label="创新奖" />
              <el-option value="优秀奖" label="优秀奖" />
              <el-option value="荣誉称号" label="荣誉称号" />
              <el-option value="其他" label="其他" />
            </el-select>
          </el-form-item>
        </div>
      </div>

      <!-- 颁奖信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">颁奖信息</h3>
        <div class="space-y-4">
          <el-form-item label="颁奖单位" prop="award_unit" required>
            <el-input
              v-model="formData.award_unit"
              placeholder="请输入颁奖单位"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="获奖时间" prop="award_time" required>
            <el-date-picker
              v-model="formData.award_time"
              type="date"
              placeholder="选择获奖时间"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="custom-input w-full"
              style="width: 100%"
            />
          </el-form-item>
          <el-form-item label="证书编号">
            <el-input
              v-model="formData.certificate_no"
              placeholder="请输入证书编号"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 其他信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">其他信息</h3>
        <div class="space-y-4">
          <el-form-item label="备注">
            <el-input
              v-model="formData.remarks"
              type="textarea"
              :rows="3"
              placeholder="请输入备注信息"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 佐证材料 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">佐证材料</h3>
        <FileUpload 
          ref="uploadRef"
          title="佐证材料上传" 
          description="请上传获奖证书或相关证明材料" 
          @success="onUploadSuccess"
          @change="onUploadChange"
        />
      </div>

      <!-- 提交按钮 -->
      <div class="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <el-button 
          @click="submitForm(true)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none border bg-white text-gray-900 hover:bg-gray-50 hover:text-gray-900 h-9 px-4 py-2"
        >
          保存草稿
        </el-button>
        <el-button 
          @click="submitForm(false)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none bg-blue-600 text-white hover:bg-blue-700 h-9 px-4 py-2 border-0"
        >
          提交
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { saveHonor, submitHonor, updateHonor, getHonor } from '../../api/honor'
import FileUpload from '../common/FileUpload.vue'
import AchievementTypeSection from '../common/AchievementTypeSection.vue'
import { ensureValidTeamMembers } from '../../utils/teamMembers'
import { useRoute } from 'vue-router'
import { getMyInfo } from '../../api/user'

const formData = reactive({
  achievementType: '',
  teamMembers: [],
  honors_name: '',
  honors_level: '',
  honors_type: '',
  award_unit: '',
  award_time: '',
  certificate_no: '',
  remarks: ''
})

const currentId = ref(null)
const loading = ref(false)
const evidenceMaterial = ref('')
const evidenceMaterialList = ref([])
const myInfo = ref(null)
const formRef = ref(null)
const uploadRef = ref(null)
const LAST_DRAFT_KEY = 'honor:lastDraftId'
const DRAFT_FORM_KEY = 'honor:draftForm'
const DRAFT_STATUS_KEY = 'honor:draftStatus'

const rules = reactive({
  honors_name: [{ required: true, message: '请输入获奖名称', trigger: 'blur' }],
  honors_level: [{ required: true, message: '请选择获奖级别', trigger: 'change' }],
  honors_type: [{ required: true, message: '请选择获奖类型', trigger: 'change' }],
  award_unit: [{ required: true, message: '请输入颁奖单位', trigger: 'blur' }],
  award_time: [{ required: true, message: '请选择获奖时间', trigger: 'change' }]
})

const handleAchievementTypeChange = (value) => {
  formData.achievementType = value
}

const handleTeamMembersChange = (members) => {
  formData.teamMembers = members
}

function onUploadSuccess({ url }) {
  if (url && !evidenceMaterialList.value.includes(url)) {
    evidenceMaterialList.value.push(url)
    evidenceMaterial.value = evidenceMaterialList.value.join(',')
  }
}

function onUploadChange({ url, type }) {
  if (type === 'remove' && url) {
    evidenceMaterialList.value = evidenceMaterialList.value.filter(u => u !== url)
    evidenceMaterial.value = evidenceMaterialList.value.join(',')
  }
}

function sanitizeEvidenceMaterial(s) {
  const v = String(s || '')
  if (!v) return ''
  return v
    .split(',')
    .map(p => String(p || '').trim().replace(/`/g, ''))
    .filter(Boolean)
    .join(',')
}

function isValidUrl(u) {
  return /^https?:\/\/\S+$/.test(String(u || ''))
}

function filterValidEvidence(s) {
  const cleaned = sanitizeEvidenceMaterial(s)
  if (!cleaned) return ''
  const parts = cleaned.split(',').map(x => x.trim()).filter(Boolean)
  const valid = parts.filter(isValidUrl)
  return valid.join(',')
}

const IDENTITY_LABELS = { teacher: '教师', student: '学生', social: '社会人士' }
function toIdentityLabel(v) {
  const key = String(v || '')
  return IDENTITY_LABELS[key] ?? key
}

function normalizeAchievementType(v) {
  if (v === 'individual') return 1
  if (v === 'team') return 2
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function denormalizeAchievementType(v) {
  if (v === 1 || v === '1') return 'individual'
  if (v === 2 || v === '2') return 'team'
  return ''
}

function normalizeMembers(members = []) {
  return (Array.isArray(members) ? members : []).map((m, idx) => ({
    position: m?.rank != null ? parseInt(m.rank, 10) || idx + 1 : idx + 1,
    name: m?.name || '',
    identity: toIdentityLabel(m?.identity),
    unit: m?.unit || ''
  }))
}

function formatDateTimePlain(input) {
  if (!input) return undefined
  const d = typeof input === 'string' ? new Date(input) : input
  if (!(d instanceof Date) || isNaN(d.getTime())) return undefined
  const pad = (n) => String(n).padStart(2, '0')
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const h = pad(d.getHours())
  const min = pad(d.getMinutes())
  const s = pad(d.getSeconds())
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

function mapFormToApi(data, isDraft, id) {
  const awardTime = formatDateTimePlain(data.award_time)
  const dto = {
    id: id != null ? Number(id) : undefined,
    achievementType: normalizeAchievementType(data.achievementType),
    awardName: data.honors_name || '',
    awardLevel: data.honors_level || '',
    awardType: data.honors_type || '',
    awardOrganization: data.award_unit || '',
    certificateNumber: data.certificate_no || '',
    remark: data.remarks || '',
    evidenceMaterial: filterValidEvidence(evidenceMaterial.value || ''),
    status: isDraft ? 1 : 2,
    memberList: normalizeMembers(data.teamMembers)
  }
  if (awardTime) dto.awardTime = awardTime
  if (dto.achievementType === 1 && (!Array.isArray(dto.memberList) || dto.memberList.length === 0)) {
    const info = myInfo.value || {}
    const name = String(info?.name || '')
    const identity = String(info?.role || '教师')
    const unit = String(info?.college || '')
    dto.memberList = name || unit ? [{ position: 1, name, identity, unit }] : []
  }
  return dto
}

function mapApiToForm(dto = {}) {
  formData.achievementType = denormalizeAchievementType(dto.achievementType)
  formData.teamMembers = Array.isArray(dto.memberList)
    ? dto.memberList.map((m, i) => ({
        id: `${Date.now()}_${i}`,
        name: String(m?.name || ''),
        identity: String(m?.identity || ''),
        unit: String(m?.unit || ''),
        rank: Number(m?.position ?? i + 1) || i + 1
      }))
    : []

  formData.honors_name = dto.awardName ?? ''
  formData.honors_level = dto.awardLevel ?? ''
  formData.honors_type = dto.awardType ?? ''
  formData.award_unit = dto.awardOrganization ?? ''
  formData.award_time = dto.awardTime ? new Date(dto.awardTime) : ''
  formData.certificate_no = dto.certificateNumber ?? ''
  formData.remarks = dto.remark ?? ''
  const em = dto.evidenceMaterial || ''
  evidenceMaterial.value = em
  evidenceMaterialList.value = em ? String(em).split(',').filter(Boolean) : []
}

const submitForm = async (isDraft = false) => {
  try {
    loading.value = true
    const ok = await (formRef.value?.validate?.() ?? Promise.resolve(true)).catch(() => false)
    if (!ok) {
      ElMessage.error('请完善所有必填项')
      loading.value = false
      return
    }
    if (!formData.achievementType) {
      ElMessage.error('请选择成果归属')
      loading.value = false
      return
    }
    if (formData.achievementType === 'team') {
      const { ok, message } = ensureValidTeamMembers(formData.achievementType, formData.teamMembers)
      if (!ok) {
        ElMessage.error(message || '团队成员信息未填写完整')
        loading.value = false
        return
      }
    }
    const base = mapFormToApi(formData, isDraft, currentId.value)
    if (!currentId.value) {
      if (isDraft) {
        const resp = await saveHonor(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      } else {
        const resp = await submitHonor(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      }
    } else {
      const updateDto = { ...base, id: Number(currentId.value), status: isDraft ? 1 : 2 }
      await updateHonor(updateDto)
    }
    if (isDraft) {
      if (!currentId.value) {
        try {
          localStorage.setItem(LAST_DRAFT_KEY, String(currentId.value))
          localStorage.setItem(DRAFT_STATUS_KEY, '1')
          const snapshot = {
            form: { ...formData },
            evidenceMaterial: evidenceMaterial.value || '',
            evidenceMaterialList: Array.isArray(evidenceMaterialList.value) ? [...evidenceMaterialList.value] : [],
            currentId: currentId.value || null
          }
          localStorage.setItem(DRAFT_FORM_KEY, JSON.stringify(snapshot))
        } catch (e) { console.warn(e) }
      }
    } else {
      try { await nextTick(); formRef.value?.clearValidate?.(); formRef.value?.resetFields?.() } catch (e) { console.warn(e) }
      currentId.value = null
      try { localStorage.removeItem(LAST_DRAFT_KEY) } catch (e) { console.warn(e) }
      try { localStorage.removeItem(DRAFT_FORM_KEY) } catch (e) { console.warn(e) }
      try { localStorage.removeItem(DRAFT_STATUS_KEY) } catch (e) { console.warn(e) }
      try { resetForm() } catch (e) { console.warn(e) }
    }
    ElMessage.success(isDraft ? '草稿保存成功' : '提交成功')
  } catch {
    ElMessage.error(isDraft ? '草稿保存失败' : '提交失败')
  } finally {
    loading.value = false
  }
}

const route = useRoute()
onMounted(async () => {
  try {
    const info = await getMyInfo()
    const data = info?.data ?? info
    myInfo.value = data
  } catch (e) { console.warn(e) }
  const rid = route.query?.detailId || route.query?.id  // ← 优先用 detailId
  if (rid) {
    try {
      currentId.value = rid
      const dto = await getHonor(rid)
      mapApiToForm(dto?.data ?? dto)
    } catch (e) { console.warn(e) }
  } else {
    try {
      const draftFlag = localStorage.getItem(DRAFT_STATUS_KEY)
      if (draftFlag === '1') {
        const raw = localStorage.getItem(DRAFT_FORM_KEY)
        if (raw) {
          try {
            const snapshot = JSON.parse(raw)
            const f = snapshot?.form || {}
            Object.assign(formData, f)
            evidenceMaterial.value = snapshot?.evidenceMaterial || ''
            evidenceMaterialList.value = Array.isArray(snapshot?.evidenceMaterialList) ? [...snapshot.evidenceMaterialList] : []
            const id = snapshot?.currentId || localStorage.getItem(LAST_DRAFT_KEY)
            if (id) currentId.value = id
          } catch (e) { console.warn(e) }
        }
      }
    } catch (e) { console.warn(e) }
  }
})
const resetForm = () => {
  Object.assign(formData, {
    achievementType: '',
    isFirstAuthor: '',
    isFirstInUnit: '',
    teamMembers: [],
    honors_name: '',
    honors_level: '',
    honors_type: '',
    award_unit: '',
    certificate_no: '',
    award_time: '',
    remarks: ''
  })
  evidenceMaterial.value = ''
  evidenceMaterialList.value = []
  currentId.value = null
  try { uploadRef.value?.clear?.() } catch (e) { console.warn(e) }
}
</script>

<style scoped>
/* 自定义Select样式 */
:deep(.custom-select .el-select__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-select .el-select__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-select .el-select__wrapper.is-focused) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-select .el-select__placeholder) {
  color: #9ca3af;
}

:deep(.custom-select .el-select__input) {
  color: #111827;
  background-color: transparent;
}

/* 自定义Input样式，与Select保持一致 */
:deep(.custom-input .el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-input .el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-input .el-input__inner) {
  color: #111827;
  background-color: transparent;
}

:deep(.custom-input .el-input__inner::placeholder) {
  color: #9ca3af;
}

/* Textarea样式 */
:deep(.custom-input .el-textarea__inner) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #111827;
}

:deep(.custom-input .el-textarea__inner:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-textarea__inner:focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-input .el-textarea__inner::placeholder) {
  color: #9ca3af;
}

/* 日期选择器样式 */
:deep(.custom-input .el-date-editor.el-input) {
  width: 100%;
}

:deep(.custom-input .el-date-editor .el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-input .el-date-editor .el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-date-editor .el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}
</style>